/**
 * ADR-0181 — document series helpers.
 *
 * Pure functions, extracted so they can be tested: the console test glob is
 * `*.test.ts` and there is no React renderer, so route logic that needs
 * testing lives here (the `lib/semanticTemplateLayer.ts` precedent).
 */

/**
 * The display name a NEW series defaults to: the filename with its extension
 * and any trailing `_v<N>` / `-v<N>` / ` v<N>` stripped, because that suffix is
 * the human's own version marker and the series is what survives across it.
 *
 * AUTHORITY NOTE. The hub owns this rule for callers that send no name (the
 * door's `land_document`, `deriveSeriesName` in `studies/types.ts`). This copy
 * exists only to PRE-FILL an editable field, so the user sees and can change
 * the name before it is written — and the console then sends what they
 * confirmed. The two can differ without either being wrong, because whatever
 * the user sees is what gets sent; they are not two implementations of one
 * decision.
 */
export function defaultSeriesName(filename: string): string {
  const base = filename.split("/").pop() ?? filename;
  const withoutExt = base.replace(/\.[A-Za-z0-9]{1,8}$/, "");
  const withoutVersion = withoutExt.replace(/[ _-]v\d+$/i, "");
  return (withoutVersion.trim() || withoutExt.trim() || base).slice(0, 120);
}

/**
 * ADR-0181 — the marker a backfilled version carries when the ledger could not
 * name who landed it. Kept in full in the record (it explains itself there);
 * shortened for a table cell, where the full sentence would distort the
 * column. The explanation moves to the cell's `title`.
 */
export const UNATTRIBUTED_LANDING = "unattributed (landed before version tracking)";

export function landerLabel(attribution: string): { text: string; title?: string } {
  if (attribution === UNATTRIBUTED_LANDING) {
    return { text: "unattributed", title: "Landed before Atlas tracked document versions, so the record does not name who landed it." };
  }
  return { text: attribution.replace(/^(account|key|capture|escalation|system):/, "") };
}
